import {
  SEARCH_TYPE_CARE,
  SEARCH_TYPE_HOME_CARE,
  SEARCH_TYPE_HOSPITAL,
  SEARCH_TYPE_MEDICAL_SUPPLIES,
  SEARCH_TYPE_PSYCHIATRIC_CARE,
  SEARCH_TYPE_REHABILITATION,
} from "core/consts";
import { SearchType } from "core/types";
// TODO: move ds/ui form prop types to core or use generic types
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { CheckboxGroupProps, RadioGroupProps, SelectProps } from "ds/ui";
import { useCallback, useMemo } from "react";
import { useTranslations } from "translations";
import { OntologyType } from ".";
import { getOntologies, getOntologiesForSelect, getOntology } from "./utils";

export const useGetOntology = () => {
  const translations = useTranslations();
  return ({
    key,
    type,
    useShortValues,
  }: {
    key: number | null | undefined;
    type: OntologyType;
    useShortValues?: boolean;
  }) => getOntology(type, key, translations, useShortValues);
};

/**
 * @returns an array of ontologies formatted for MUI Select component
 */
export const useGetOntologiesForSelect = ({
  sort = false,
  type,
}: {
  sort?: boolean;
  type: OntologyType;
}) => {
  const translations = useTranslations();

  return getOntologiesForSelect(type, sort, translations);
};

export const useGetOntologies = () => {
  const translations = useTranslations();

  return ({ sort = false, type }: { sort?: boolean; type: OntologyType }) =>
    getOntologies(type, sort, translations);
};

type ReceiverTypes =
  | "receiverTypePlural"
  | "receiverTypePluralPreferred"
  | "receiverTypeSingular"
  | "receiverTypeSingularPreferred";

export type GetTranslationForSearchTypeArgs = {
  receiverType: ReceiverTypes;
  searchType: SearchType | null | undefined;
  translationKey: string;
};

export const useGetTranslationForSearchType = () => {
  const translations = useTranslations();
  const getOntology = useGetOntology();

  const getTranslationForSearchType = useCallback(
    ({
      receiverType,
      searchType,
      translationKey,
    }: GetTranslationForSearchTypeArgs) => {
      if (
        !searchType ||
        ![
          SEARCH_TYPE_CARE,
          SEARCH_TYPE_HOSPITAL,
          SEARCH_TYPE_PSYCHIATRIC_CARE,
          SEARCH_TYPE_REHABILITATION,
          SEARCH_TYPE_MEDICAL_SUPPLIES,
          SEARCH_TYPE_HOME_CARE,
        ].includes(searchType)
      ) {
        searchType = SEARCH_TYPE_CARE;
      }
      try {
        const receiverTypeOntology = getOntology({
          key: searchType,
          type: receiverType,
        });

        const keys = translationKey.split(".");

        let translationObj: any = translations;

        keys.forEach((key: string) => {
          translationObj = translationObj[key];
        });

        return translationObj({ [receiverType]: receiverTypeOntology });
      } catch (error) {
        console.error(
          `Failed to return translation for key ${translationKey} searchType ${searchType}`,
          error,
        );
      }
    },
    [translations, getOntology],
  );

  return getTranslationForSearchType;
};

export function TranslationForSearchType({
  receiverType,
  searchType,
  translationKey,
}: {
  receiverType: ReceiverTypes;
  searchType: SearchType;
  translationKey: string;
}) {
  const getTranslationForSearchType = useGetTranslationForSearchType();
  return getTranslationForSearchType({
    receiverType,
    searchType,
    translationKey,
  });
}

export const useGetOntologyOptions = <
  T extends "radio" | "select" | "checkbox" = "radio",
>({
  format = "radio" as T,
  sort,
  type,
}: {
  format?: T;
  sort?: boolean;
  type: OntologyType;
}): T extends "select"
  ? SelectProps<{ id: string; label: string; value: string }>["items"]
  : T extends "checkbox"
  ? CheckboxGroupProps<string | number>["options"]
  : RadioGroupProps<number>["options"] => {
  const getOntologies = useGetOntologies();
  return useMemo(
    () =>
      getOntologies({ sort, type }).map((ontology) => {
        const baseOption = {
          label: ontology.label,
          value: ontology.value,
        };

        if (format === "select") {
          return {
            ...baseOption,
            id: String(ontology.value),
            value: String(ontology.value),
          };
        }

        if (format === "checkbox") {
          return {
            ...baseOption,
          };
        }

        return {
          ...baseOption,
          id: ontology.value,
        };
      }) as T extends "select"
        ? SelectProps<{ id: string; label: string; value: string }>["items"]
        : T extends "checkbox"
        ? CheckboxGroupProps<string | number>["options"]
        : RadioGroupProps<number>["options"],
    [getOntologies, format, sort, type],
  );
};

export const useGetOntologyRadioOptions = ({
  sort,
  type,
}: {
  sort?: boolean;
  type: OntologyType;
}): RadioGroupProps<number>["options"] => {
  return useGetOntologyOptions({ format: "radio", sort, type });
};
